class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-discount-form]');
    this.input = this.querySelector('[data-discount-input]');
    this.applyButton = this.querySelector('[data-discount-apply]');
    this.appliedContainer = this.querySelector('[data-discount-applied]');
    this.shippingContainer = this.querySelector('[data-discount-shipping]');
    this.loadingIndicator = this.querySelector('[data-discount-loading]');
    this.context = this.dataset.context || 'drawer';
    this.currentDiscounts = this.getCurrentDiscounts();

    this.debouncedApply = theme.utils.debounce(this.applyDiscountCode.bind(this), 500);
    this.debouncedRemove = theme.utils.debounce(this.removeDiscountCode.bind(this), 500);
    this.debouncedRemoveAll = theme.utils.debounce(this.removeAllDiscountCodes.bind(this), 500);
  }

  connectedCallback() {
    this._eventsController = new AbortController();
    this.attachEvents();
    this.checkShippingDiscounts();
    this.subscribeToCartUpdates();
  }

  disconnectedCallback() {
    this._eventsController?.abort();
    this._cartUpdateUnsub?.();
  }

  attachEvents() {
    const { signal } = this._eventsController;

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.debouncedApply();
    }, { signal });

    this.querySelectorAll('[data-discount-remove]').forEach((button) => {
      button.addEventListener('click', (e) => {
        const code = e.currentTarget.dataset.discountRemove;
        this.debouncedRemove(code);
      }, { signal });
    });

    this.querySelector('[data-discount-remove-all]')?.addEventListener('click', () => {
      this.debouncedRemoveAll();
    }, { signal });
  }

  subscribeToCartUpdates() {
    if (typeof theme !== 'undefined' && theme.pubsub) {
      this._cartUpdateUnsub = theme.pubsub.subscribe(theme.pubsub.events.CART_UPDATE, (data) => {
        if (data?.skipFetch || data?.sections) return;
        this.refreshFromCart();
      });
    }
  }

  async refreshFromCart() {
    try {
      const sectionId = this.getSectionsToRender();
      const cart = await theme.cart.fetch({ sections: [sectionId], sectionsUrl: window.location.pathname });
      this.currentDiscounts = (cart.cart_level_discount_applications || [])
        .map(d => d.title || d.discount_application?.title)
        .filter(Boolean);
      if (cart.sections) {
        this.renderSections(cart.sections);
        this.renderShippingDiscounts(cart);
      }
    } catch (error) {
      console.error('Error refreshing cart discount:', error);
    }
  }

  getCurrentDiscounts() {
    const codes = [];
    this.querySelectorAll('[data-discount-code]').forEach((el) => {
      codes.push(el.dataset.discountCode);
    });
    return codes;
  }

  async applyDiscountCode() {
    const code = this.input.value.trim();

    if (!code) {
      theme.cart.errors.setFooter(theme.locales.cart.footerError.invalidDiscount, 'invalid_discount');
      theme.pubsub.publish(theme.pubsub.events.CART_FOOTER_ERROR, {
        message: theme.locales.cart.footerError.invalidDiscount,
        type: 'invalid_discount',
      });
      return;
    }

    theme.cart.errors.clearFooter();
    this._clearFooterErrorSlot();
    this.setLoading(true);

    try {
      const newDiscounts = [...this.currentDiscounts, code].join(',');
      const sectionIds = this.getSectionsToRender();

      const response = await theme.cart.update(
        { discount: newDiscounts },
        { sections: [sectionIds], sectionsUrl: window.location.pathname }
      );

      if (response.sections) {
        const submittedEntry = (response.discount_codes || []).find(
          (dc) => dc.code.toLowerCase() === code.toLowerCase()
        );

        if (submittedEntry?.applicable === false) {
          this.renderSections(response.sections);
          const msg = theme.locales.cart.footerError.invalidDiscount;
          theme.cart.errors.setFooter(msg, 'invalid_discount');
          theme.pubsub.publish(theme.pubsub.events.CART_FOOTER_ERROR, {
            message: msg,
            type: 'invalid_discount',
          });
          const newElement = document.querySelector(`cart-discount[data-context="${this.context}"]`);
          const newInput = newElement?.querySelector('[data-discount-input]');
          if (newInput) newInput.value = code;
          newElement?.querySelector('theme-accordion')?.open();
        } else {
          this.renderSections(response.sections);
          this.renderShippingDiscounts(response);
          this.input.value = '';
          if (theme.settings?.cartActionToastsEnable) {
            theme.toast?.show({ type: 'success', message: theme.locales.cart.success?.discountApplied || theme.locales.cart.toast.couponApplied });
          }
          theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { cart: response, skipFetch: true });
        }
      }
    } catch (error) {
      if (error?.type === 'generic' || !error?.type) {
        const msg = theme.locales.cart.footerError.invalidDiscount;
        theme.cart.errors.setFooter(msg, 'invalid_discount');
        theme.pubsub.publish(theme.pubsub.events.CART_FOOTER_ERROR, {
          message: msg,
          type: 'invalid_discount',
        });
      }
    } finally {
      this.setLoading(false);
    }
  }

  async removeDiscountCode(codeToRemove) {
    theme.cart.errors.clearFooter();
    this._clearFooterErrorSlot();
    this.setLoading(true);

    try {
      const remainingDiscounts = this.currentDiscounts
        .filter((code) => code !== codeToRemove)
        .join(',');
      const sectionIds = this.getSectionsToRender();

      const response = await theme.cart.update(
        { discount: remainingDiscounts },
        { sections: [sectionIds], sectionsUrl: window.location.pathname }
      );

      if (response.sections) {
        this.renderSections(response.sections);
        this.renderShippingDiscounts(response);
        theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { cart: response, skipFetch: true });
      }
    } catch (error) {
      const msg = theme.locales.cart.footerError.generic;
      theme.cart.errors.setFooter(msg, 'generic');
      theme.pubsub.publish(theme.pubsub.events.CART_FOOTER_ERROR, {
        message: msg,
        type: 'generic',
      });
    } finally {
      this.setLoading(false);
    }
  }

  async removeAllDiscountCodes() {
    theme.cart.errors.clearFooter();
    this._clearFooterErrorSlot();
    this.setLoading(true);

    try {
      const sectionIds = this.getSectionsToRender();

      const response = await theme.cart.update(
        { discount: '' },
        { sections: [sectionIds], sectionsUrl: window.location.pathname }
      );

      if (response.sections) {
        this.renderSections(response.sections);
        this.renderShippingDiscounts(response);
        theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { cart: response, skipFetch: true });
      }
    } catch (error) {
      const msg = theme.locales.cart.footerError.generic;
      theme.cart.errors.setFooter(msg, 'generic');
      theme.pubsub.publish(theme.pubsub.events.CART_FOOTER_ERROR, {
        message: msg,
        type: 'generic',
      });
    } finally {
      this.setLoading(false);
    }
  }

  getSectionsToRender() {
    if (this.context === 'page') {
      return 'cart';
    }
    return 'cart-drawer';
  }

  renderSections(sections) {
    const sectionKey = this.context === 'page' ? 'cart' : 'cart-drawer';
    const html = sections[sectionKey];

    if (!html) return;

    const doc = theme.utils.domParser.parseFromString(html, 'text/html');

    if (this.context === 'page') {
      this.renderCartPage(doc);
    } else {
      this.renderCartDrawer(doc);
    }
  }

  renderCartPage(doc) {
    const newContent = doc.querySelector('[data-cart-content]');
    const currentContent = document.querySelector('[data-cart-content]');

    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
    }

    this.updateCartCount(doc);
  }

  renderCartDrawer(doc) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const newContent = doc.querySelector('[data-cart-drawer-content]');
    const currentContent = cartDrawer.querySelector('[data-cart-drawer-content]');

    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
    }

    const newFooter = doc.querySelector('.cart-drawer__footer');
    const currentFooter = cartDrawer.querySelector('.cart-drawer__footer');

    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
    }

    this.updateCartCount(doc);
    cartDrawer.updateFocusableElements?.();
  }

  updateCartCount(doc) {
    const newCount = doc.querySelector('[data-cart-count]');
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (newCount) {
        el.textContent = newCount.textContent;
      }
    });
  }

  setLoading(isLoading) {
    if (this.loadingIndicator) {
      this.loadingIndicator.hidden = !isLoading;
    }

    if (this.form) {
      const formElements = this.form.elements;
      Array.from(formElements).forEach((el) => {
        el.disabled = isLoading;
      });
    }

    this.querySelectorAll('[data-discount-remove], [data-discount-remove-all]').forEach((button) => {
      button.disabled = isLoading;
    });

    this.style.pointerEvents = isLoading ? 'none' : '';
    this.style.opacity = isLoading ? '0.6' : '1';
  }

  _clearFooterErrorSlot() {
    const slot = document.querySelector('[data-cart-footer-error]');
    if (!slot) return;
    slot.hidden = true;
    slot.textContent = '';
  }

  getTranslation(key) {
    const translations = {
      remove_label: this.dataset.removeLabel,
      applied_at_checkout: this.dataset.appliedAtCheckout,
    };
    return translations[key] || '';
  }

  async checkShippingDiscounts() {
    try {
      const cart = await theme.cart.fetch();
      this.renderShippingDiscounts(cart);
    } catch (error) {
      console.error('Error checking shipping discounts:', error);
    }
  }

  renderShippingDiscounts(cart) {
    if (!this.shippingContainer) return;

    const cartLevelCodes = (cart.cart_level_discount_applications || [])
      .map((d) => (d.title || d.discount_application?.title || '').toLowerCase())
      .filter(Boolean);

    const shippingDiscounts = (cart.discount_codes || []).filter((dc) => {
      const isApplicable = dc.applicable;
      const isNotCartLevel = !cartLevelCodes.includes(dc.code.toLowerCase());
      return isApplicable && isNotCartLevel;
    });

    if (shippingDiscounts.length === 0) {
      this.shippingContainer.hidden = true;
      this.shippingContainer.innerHTML = '';
      return;
    }

    const removeLabel = this.getTranslation('remove_label');
    const iconTemplate = this.querySelector('[data-discount-icon]');

    this.shippingContainer.innerHTML = '';
    shippingDiscounts.forEach((dc) => {
      const chip = document.createElement('div');
      chip.className = 'cart-discount__code';
      chip.dataset.discountCode = dc.code;

      const label = document.createElement('span');
      label.className = 'cart-discount__code-label';
      label.textContent = dc.code;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-discount__code-remove';
      button.setAttribute('aria-label', `${removeLabel} ${dc.code}`);
      button.dataset.discountRemove = dc.code;

      if (iconTemplate) {
        button.appendChild(iconTemplate.content.cloneNode(true));
      }

      chip.appendChild(label);
      chip.appendChild(button);
      this.shippingContainer.appendChild(chip);
    });

    this.shippingContainer.hidden = false;
    this.attachShippingDiscountEvents();
  }

  attachShippingDiscountEvents() {
    const { signal } = this._eventsController;
    this.shippingContainer?.querySelectorAll('[data-discount-remove]').forEach((button) => {
      button.addEventListener('click', (e) => {
        const code = e.currentTarget.dataset.discountRemove;
        this.debouncedRemove(code);
      }, { signal });
    });
  }
}

customElements.define('cart-discount', CartDiscount);
